import React from "react";
import PropTypes from "prop-types";

import FormInput from "./FormInput";
import FormSelect from "./FormSelect";
import FormDatePicker from "./ReactFlatpickr";
import FormTextarea from "./FormTextarea";
import FormNumberInput from "./FormNumberInput";
import FormInputPassword from "./FormInputPassword";
import FormPhoneInput from "./FormPhoneInput";
import FormCheckbox from "./FormCheckbox";
import FormRadio from "./FormRadio";
import FormInputFile from "./FormInputFile";
import FormMultipleInputFile from "./FormMultipleInputFile";
import Switch from "./Switch";

import { getSelectOptions } from "utils/optionHelper";

const FormRenderer = ({
  fields = [],
  values = {},
  errors = {},
  handleChange,
  optionConfig = {},
  switchConfig = {
    ValueOne: "1",
    ValueTwo: "0",
    LabelOne: "Active",
    LabelTwo: "Inactive",
  },
  extraValues = {}, // For additional values like billingRates, commissionRate, etc.
}) => {

  const renderField = (field) => {
    if (!field || !field.name) return null;

    // Handle showWhen property for conditional rendering
    if (field.showWhen) {
      const condition = field.showWhen;
      // Support equality comparisons like "payment_method===9"
      if (condition.includes("===")) {
        const [depField, expectedValue] = condition.split("===");
        if (String(values?.[depField]) !== String(expectedValue)) return null;
      }
      // Simple evaluation: if condition starts with "!", check if value is falsy
      else if (condition.startsWith("!")) {
        const depField = condition.slice(1);
        if (values?.[depField]) return null;
      } else {
        if (!values?.[condition]) return null;
      }
    }

    // Handle disabledWhen property for conditional disabling
    let isDisabled = field.disabled || false;
    if (field.disabledWhen) {
      const condition = field.disabledWhen;
      // Simple evaluation: if condition starts with "!", check if value is falsy
      if (condition.startsWith("!")) {
        const depField = condition.slice(1);
        isDisabled = !values?.[depField];
      } else {
        isDisabled = !!values?.[condition];
      }
    }

    // Handle valueFrom property for custom value transformations
    let fieldValue = values?.[field.name] ?? "";
    if (field.valueFrom && extraValues) {
      fieldValue = extraValues[field.valueFrom] ?? fieldValue;
    }

    const commonProps = {
      field,
      value: fieldValue,
      handleChange,
      error: errors?.[field.name] ?? null,
      disabled: isDisabled,
    };

    const fileProps = {
      ...commonProps,
      imagePreview:
        values?.[`${field.name}_preview`] ?? values?.[field.name] ?? null,
    };

    switch (field.type) {
      case "select":
        return (
          <FormSelect
            key={field.id || field.name}
            {...commonProps}
            options={getSelectOptions(field.name, optionConfig)}
          />
        );

      case "textarea":
        return <FormTextarea key={field.id || field.name} {...commonProps} />;

      case "date":
      case "datetime":
      case "monthYear":
        return (
          <FormDatePicker
            {...commonProps}
            key={field.id || field.name}
            mode={field.type}
            selectMode={field.selectMode || "single"}
            disablePast={field.disablePast}
            disablePastMonth={field.disablePastMonth}
            yearLimit={field.yearLimit}
          />
        );

    
      case "password":
        return <FormInputPassword
          key={field.id || field.name}
          {...commonProps} />;

      case "phone":
        return (
          <FormPhoneInput
            key={field.id || field.name}
            {...commonProps}
            country_code={values?.[field.country_code_field || "country_dial_code"]}
            country_code_field={field.country_code_field}
          />
        );

      case "checkbox":
        return <FormCheckbox
          key={field.id || field.name}
          {...commonProps} />;

      case "radio":
        return <FormRadio
          key={field.id || field.name}
          {...commonProps} />;

      case "file":
        return field.multiple ? (
          <FormMultipleInputFile
            key={field.id || field.name}
            {...fileProps} />
        ) : (
          <FormInputFile
            key={field.id || field.name}
            {...fileProps} />
        );

      case "multi-file":
        return <FormMultipleInputFile
          key={field.id || field.name}
          {...fileProps} />;

      case "switch":
        return <Switch
          key={field.id || field.name}
          {...commonProps}
          values={values?.[field.name] ?? ""}
          {...(field.switchConfig || switchConfig)}
        />;

      default:
        return <FormInput
          key={field.id || field.name}
          {...commonProps} />;
    }
  };

  return (
    <div className="row g-3">
      {fields.map((field) => renderField(field))}
    </div>
  );
};

FormRenderer.propTypes = {
  fields: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  optionConfig: PropTypes.object,
};

export default React.memo(FormRenderer);
